import { useSearchParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiChevronRight } from "react-icons/fi";
import ProductDetailLayout from "./ProductDetailLayout";
import { PRODUCTS_DATA } from "../../data/productsData";
import Loader from "../../common/Loader";
import LazyImage from "../../common/LazyImage";
import logo from "../../assets/logo.png";
export default function ProductsPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const catParam = params?.get("cat");
  const subParam = params?.get("sub");

  const [selectedCat, setSelectedCat] = useState(catParam || "all");
  const [currentImage, setCurrentImage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const AZURE_BASE_URL =
    "https://hlineweb-f6dwh8btedejg7fz.z03.azurefd.net//hlinefiles/HLineWeb/Projects/assets/projects/windows/";

  const images = [
    `${AZURE_BASE_URL}CHL00625-HDR.jpg`,
    `${AZURE_BASE_URL}CHL00652-HDR.jpg`,
    `${AZURE_BASE_URL}CHL00695-HDR.jpg`,
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const productThumbs = [];
    Object.values(PRODUCTS_DATA).forEach((cat) => {
      Object.values(cat.subcategories).forEach((sub) => {
        if (sub.thumbnail) productThumbs.push(sub.thumbnail);
      });
    });

    const allImages = [...images, ...productThumbs];
    let loaded = 0;

    allImages.forEach((src) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        loaded++;
        if (loaded === allImages.length) setIsLoading(false);
      };
      img.onerror = () => {
        loaded++;
        if (loaded === allImages.length) setIsLoading(false);
      };
    });
  }, []);

  useEffect(() => {
    setSelectedCat(catParam || "all");
  }, [catParam]);

  const handleFilterClick = (cat) => {
    if (cat === "all") navigate("/products");
    else navigate(`/products?cat=${cat}`);
  };

  const filteredData =
    selectedCat === "all"
      ? PRODUCTS_DATA
      : { [selectedCat]: PRODUCTS_DATA[selectedCat] };

  if (catParam && subParam) {
    const category = PRODUCTS_DATA[catParam];
    const subcategoryKeys = Object.keys(category?.subcategories || {});
    const baseKey = "HL Imperial Slim System"; // parent with variants
    const matchedKey = subParam;

    // check if selected subcategory is a variant (belongs to baseKey)
    const isVariant =
      subcategoryKeys.includes(baseKey) &&
      category.subcategories[baseKey].variants &&
      Object.keys(category.subcategories[baseKey].variants).includes(
        matchedKey
      );

    let displayData;
    let displayTitle;

    if (isVariant) {
      // show only the specific variant
      displayData = category.subcategories[baseKey].variants[matchedKey];
      displayTitle = matchedKey;
    } else {
      // normal subcategory
      displayData = category.subcategories[matchedKey];
      displayTitle = matchedKey;
    }

    if (!displayData) return null;
    return (
      <>
        <AnimatePresence>{isLoading && <Loader />}</AnimatePresence>
        {!isLoading && (
          <div className="animate-[page-in_.35s_ease] text-white bg-transparent relative scroll-mt-24">
            <div className="container py-10">
              <div className="flex items-center gap-1 text-sm text-white/70 mb-6">
                <button
                  onClick={() => navigate("/products")}
                  className="hover:text-white underline"
                >
                  All
                </button>
                <FiChevronRight className="text-white/50" />
                <button
                  onClick={() => navigate(`/products?cat=${catParam}`)}
                  className="hover:text-white underline"
                >
                  {category.title}
                </button>
                <FiChevronRight className="text-white/50" />
                <span className="text-white">{displayTitle}</span>
              </div>

              <ProductDetailLayout
                title={displayTitle}
                subcategory={displayData}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <AnimatePresence>{isLoading && <Loader />}</AnimatePresence>

      {!isLoading && (
        <div className="text-white bg-transparent">
          <section className="relative w-full h-[60vh] overflow-hidden flex items-center justify-start">
            <AnimatePresence mode="wait">
              <motion.img
                key={currentImage}
                src={images[currentImage]}
                alt="Windows and Doors"
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.6 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
              />
            </AnimatePresence>

            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />

            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="relative z-10 container"
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold max-w-3xl">
                Architectural Systems
              </h1>
              <p className="mt-4 text-white/80 text-base sm:text-lg max-w-xl leading-relaxed">
                Explore precision-engineered aluminium windows, doors, and
                façade systems — built for durability, design, and performance
                excellence.
              </p>
            </motion.div>
          </section>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="container py-10 flex flex-wrap justify-center gap-3"
          >
            <button
              onClick={() => handleFilterClick("all")}
              className={`px-6 py-2 border text-sm font-medium backdrop-blur-md transition ${
                selectedCat === "all"
                  ? "bg-white text-black border-white"
                  : "border-white/30 text-white hover:bg-white/10"
              }`}
            >
              All Products
            </button>

            {Object.entries(PRODUCTS_DATA).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => handleFilterClick(key)}
                className={`px-6 py-2 border text-sm font-medium backdrop-blur-md transition ${
                  selectedCat === key
                    ? "bg-white text-black border-white"
                    : "border-white/30 text-white hover:bg-white/10"
                }`}
              >
                {cat.title}
              </button>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="container pb-20"
          >
            {Object.entries(filteredData).map(([catKey, cat]) => (
              <div key={catKey} className="mb-16">
                {selectedCat === "all" && (
                  <h2 className="text-3xl font-semibold mb-6 border-l-4 border-white pl-4">
                    {cat.title}
                  </h2>
                )}

                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
                  {Object.entries(cat.subcategories).map(([subKey, sub]) => (
                    <div
                      key={subKey}
                      className="group relative bg-neutral-900/80 rounded-md cursor-pointer 
                        overflow-hidden shadow-lg hover:shadow-2xl transition-transform duration-500 hover:-translate-y-1"
                      onClick={() =>
                        navigate(
                          `/products?cat=${catKey}&sub=${encodeURIComponent(
                            subKey
                          )}`
                        )
                      }
                    >
                      <div className="relative h-52 w-full overflow-hidden bg-neutral-800 flex items-center justify-center">
                        {sub.thumbnail ? (
                          <>
                            <LazyImage
                              src={sub.thumbnail}
                              alt={subKey}
                              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                          </>
                        ) : (
                          <img src={logo} alt="" className="h-16" />
                        )}
                      </div>

                      <div className="p-6 relative z-10">
                        <h3 className="text-xl font-semibold mb-2 text-white">
                          {subKey}
                        </h3>
                        <p className="text-sm text-white/70 line-clamp-3">
                          {sub.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      )}
    </>
  );
}
